import { useState } from "react";
import FullCalendar, { formatDate } from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import listPlugin from "@fullcalendar/list";
import { Box, List, ListItem, ListItemText, Typography, useTheme } from "@mui/material";
import Header from "../../components/Header";
import { tokens } from "../../theme";
import EventCardDialog from "./EventCardDialog";

const Calendar = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [currentEvents, setCurrentEvents] = useState([]);

  const [cardOpen, setCardOpen] = useState(false);
  const [selectInfo, setSelectInfo] = useState(null);
  const [editEvent, setEditEvent] = useState(null);

  const handleSelect = (info) => {
    setEditEvent(null);
    setSelectInfo(info);
    setCardOpen(true);
  };

  const handleEventClick = (info) => {
    info.jsEvent.preventDefault();
    setSelectInfo(null);
    setEditEvent(info.event);
    setCardOpen(true);
  };

  const handleCardClose = (result) => {
    if (!result || result.action === "cancel") {
      if (selectInfo?.view?.calendar) selectInfo.view.calendar.unselect();
      setCardOpen(false);
      setSelectInfo(null);
      setEditEvent(null);
      return;
    }

    if (result.action === "delete") {
      if (selectInfo?.view?.calendar) selectInfo.view.calendar.unselect();
      result.fcEvent?.remove();
      setCardOpen(false);
      setSelectInfo(null);
      setEditEvent(null);
      return;
    }

    if (result.action === "save") {
      const { fcEvent: ev, payload } = result;
      if (selectInfo?.view?.calendar) selectInfo.view.calendar.unselect();

      if (ev) {
        ev.setProp("title", payload.title);
        ev.setAllDay(payload.allDay);
        ev.setStart(payload.start);
        ev.setEnd(payload.end);
        const xp = payload.extendedProps || {};
        Object.keys(xp).forEach((key) => {
          ev.setExtendedProp(key, xp[key]);
        });
      } else if (selectInfo?.view?.calendar) {
        selectInfo.view.calendar.addEvent({
          id: `evt-${Date.now()}`,
          title: payload.title,
          start: payload.start,
          end: payload.end,
          allDay: payload.allDay,
          extendedProps: payload.extendedProps,
        });
      }

      setCardOpen(false);
      setSelectInfo(null);
      setEditEvent(null);
    }
  };

  return (
    <Box m="20px">
      <Header title="Calendar" subtitle="Full Calendar Interactive Page" />

      <Box display="flex" justifyContent="space-between">
        <Box flex="1 1 20%" backgroundColor={colors.primary[400]} p="15px" borderRadius="4px">
          <Typography variant="h5">Events</Typography>
          <List>
            {currentEvents.map((event) => {
              const labs = event.extendedProps?.labels;
              const labelStr = Array.isArray(labs) && labs.length ? ` · ${labs.join(", ")}` : "";
              return (
                <ListItem
                  key={event.id}
                  sx={{
                    backgroundColor: colors.greenAccent[500],
                    margin: "10px 0",
                    borderRadius: "2px",
                  }}
                >
                  <ListItemText
                    primary={event.title}
                    secondary={
                      <Typography component="span" variant="body2">
                        {formatDate(event.start, {
                          year: "numeric",
                          month: "short",
                          day: "numeric",
                        })}
                        {labelStr}
                      </Typography>
                    }
                  />
                </ListItem>
              );
            })}
          </List>
        </Box>

        <Box flex="1 1 100%" ml="15px">
          <FullCalendar
            height="75vh"
            plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin, listPlugin]}
            headerToolbar={{
              left: "prev,next today",
              center: "title",
              right: "dayGridMonth,timeGridWeek,timeGridDay,listMonth",
            }}
            initialView="dayGridMonth"
            editable
            selectable
            selectMirror
            dayMaxEvents
            select={handleSelect}
            eventClick={handleEventClick}
            eventsSet={(events) => setCurrentEvents(events)}
            initialEvents={[
              { id: "12315", title: "All-day event", date: "2026-05-14" },
              { id: "5123", title: "Timed event", start: "2026-05-28T10:00:00", end: "2026-05-28T12:00:00" },
            ]}
          />
        </Box>
      </Box>

      <EventCardDialog open={cardOpen} onClose={handleCardClose} fcEvent={editEvent} selectInfo={selectInfo} />
    </Box>
  );
};

export default Calendar;
